import { CloseOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { useMemo, useRef, useState } from 'react'
import { workingHours } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import Button from '@UI/New/Button'
import { normalizeWorkHours, WORK_DAYS } from './WorkHoursSchedule'
import classes from './index.module.css'

const timeToMinutes = (time) => {
  if (!/^\d{2}:\d{2}$/.test(time ?? '')) return null

  const [hours, minutes] = time.split(':').map(Number)
  if (hours > 23 || minutes > 59) return null

  return hours * 60 + minutes
}

const intervalsOverlap = (first, second) => {
  const firstStart = timeToMinutes(first.start_time)
  const firstEnd = timeToMinutes(first.end_time)
  const secondStart = timeToMinutes(second.start_time)
  const secondEnd = timeToMinutes(second.end_time)

  if ([firstStart, firstEnd, secondStart, secondEnd].includes(null)) {
    return false
  }

  return firstStart < secondEnd && secondStart < firstEnd
}

const getIntervalError = (interval, intervals) => {
  const start = timeToMinutes(interval.start_time)
  const end = timeToMinutes(interval.end_time)

  if (start === null || end === null) return 'Укажите начало и окончание'
  if (start >= end) return 'Начало должно быть раньше окончания'

  const overlappingInterval = intervals.find(
    (item) =>
      item.key !== interval.key &&
      item.week_day === interval.week_day &&
      intervalsOverlap(interval, item),
  )

  if (overlappingInterval) {
    return `Пересекается с ${overlappingInterval.start_time}–${overlappingInterval.end_time}`
  }

  return ''
}

const getDefaultInterval = (dayIntervals) => {
  const sortedIntervals = [...dayIntervals]
    .filter((item) => timeToMinutes(item.end_time) !== null)
    .sort((first, second) =>
      first.end_time.localeCompare(second.end_time),
    )
  const lastInterval = sortedIntervals[sortedIntervals.length - 1]

  if (!lastInterval) return { start_time: '09:00', end_time: '18:00' }

  const start = timeToMinutes(lastInterval.end_time)
  if (start === null || start >= 23 * 60 + 59) {
    return { start_time: '', end_time: '' }
  }

  const end = Math.min(start + 60, 23 * 60 + 59)
  const formatTime = (minutes) =>
    `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(
      minutes % 60,
    ).padStart(2, '0')}`

  return {
    start_time: formatTime(start),
    end_time: formatTime(end),
  }
}

const WorkHoursEditor = ({
  userId,
  workHours = [],
  onClose,
  onSaved,
}) => {
  const addAlert = useAddAlert()
  const newIntervalId = useRef(0)
  const [intervals, setIntervals] = useState(() =>
    normalizeWorkHours(workHours).map((item) => ({
      ...item,
      key: `saved-${item.id}`,
      isNew: false,
    })),
  )
  const [loading, setLoading] = useState(false)
  const [deletingIds, setDeletingIds] = useState([])

  const newIntervals = useMemo(
    () => intervals.filter((item) => item.isNew),
    [intervals],
  )
  const errors = useMemo(() => {
    const result = {}
    newIntervals.forEach((interval) => {
      result[interval.key] = getIntervalError(interval, intervals)
    })
    return result
  }, [intervals, newIntervals])
  const hasErrors = Object.values(errors).some(Boolean)

  const addInterval = (weekDay) => {
    const dayIntervals = intervals.filter((item) => item.week_day === weekDay)
    const defaultInterval = getDefaultInterval(dayIntervals)
    newIntervalId.current += 1

    setIntervals((current) => [
      ...current,
      {
        ...defaultInterval,
        week_day: weekDay,
        key: `new-${Date.now()}-${newIntervalId.current}`,
        isNew: true,
      },
    ])
  }

  const changeInterval = (key, field, value) => {
    setIntervals((current) =>
      current.map((item) =>
        item.key === key ? { ...item, [field]: value } : item,
      ),
    )
  }

  const removeInterval = async (interval) => {
    if (interval.isNew) {
      setIntervals((current) =>
        current.filter((item) => item.key !== interval.key),
      )
      return
    }

    if (!window.confirm('Удалить этот интервал рабочего времени?')) return

    setDeletingIds((current) => [...current, interval.id])
    try {
      await workingHours.delete(interval.id)
      setIntervals((current) =>
        current.filter((item) => item.key !== interval.key),
      )
      onSaved?.()
      addAlert({ text: 'Интервал удалён' })
    } catch (error) {
      addAlert({
        type: 'error',
        title: 'Не удалось удалить интервал',
      })
    } finally {
      setDeletingIds((current) =>
        current.filter((id) => id !== interval.id),
      )
    }
  }

  const save = async () => {
    if (!newIntervals.length) {
      onClose?.()
      return
    }
    if (hasErrors) return

    setLoading(true)
    let savedCount = 0

    for (const interval of newIntervals) {
      const formData = new FormData()
      formData.append('start_time', interval.start_time)
      formData.append('end_time', interval.end_time)
      formData.append('week_day', interval.week_day)
      formData.append('user_id', userId)

      try {
        await workingHours.create(formData)
        savedCount += 1
      } catch (error) {
        // Глобальный обработчик API покажет серверную ошибку.
      }
    }

    onSaved?.()
    setLoading(false)

    if (savedCount === newIntervals.length) {
      addAlert({ text: 'Рабочее время сохранено' })
    } else {
      addAlert({
        type: 'error',
        title: 'Часть интервалов не удалось сохранить',
        text: `Сохранено ${savedCount} из ${newIntervals.length}`,
      })
    }

    onClose?.()
  }

  return (
    <div className={classes.editor}>
      <div className={classes.editorHeader}>
        <div>
          <h2 className={classes.editorTitle}>Рабочее время</h2>
          <p className={classes.editorDescription}>
            Добавьте все периоды доступности. Интервалы одного дня не должны
            пересекаться.
          </p>
        </div>
        <button
          className={classes.closeButton}
          type="button"
          onClick={onClose}
          aria-label="Закрыть"
        >
          <CloseOutlined />
        </button>
      </div>

      <div className={classes.editorDays}>
        <div className={classes.editorTableHeader}>
          <span>День недели</span>
          <span>Рабочие интервалы</span>
          <span>Новый период</span>
        </div>
        {WORK_DAYS.map((day) => {
          const dayIntervals = intervals
            .filter((item) => item.week_day === day.value)
            .sort((first, second) =>
              first.start_time.localeCompare(second.start_time),
            )

          return (
            <div className={classes.editorDay} key={day.value}>
              <div className={classes.editorDayLabel}>
                <span className={classes.dayShort}>{day.short}</span>
                <span>{day.name}</span>
              </div>

              <div className={classes.editorIntervals}>
                {!dayIntervals.length && (
                  <span className={classes.dayOff}>Выходной</span>
                )}
                {dayIntervals.map((interval) =>
                  interval.isNew ? (
                    <div className={classes.newInterval} key={interval.key}>
                      <div className={classes.timeInputs}>
                        <input
                          type="time"
                          value={interval.start_time}
                          aria-label={`Начало рабочего времени, ${day.name}`}
                          onChange={(event) =>
                            changeInterval(
                              interval.key,
                              'start_time',
                              event.target.value,
                            )
                          }
                        />
                        <span className={classes.timeSeparator}>—</span>
                        <input
                          type="time"
                          value={interval.end_time}
                          aria-label={`Окончание рабочего времени, ${day.name}`}
                          onChange={(event) =>
                            changeInterval(
                              interval.key,
                              'end_time',
                              event.target.value,
                            )
                          }
                        />
                        <button
                          type="button"
                          className={classes.deleteButton}
                          onClick={() => removeInterval(interval)}
                          aria-label="Удалить интервал"
                        >
                          <DeleteOutlined />
                        </button>
                      </div>
                      {errors[interval.key] && (
                        <span className={classes.intervalError}>
                          {errors[interval.key]}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className={classes.savedInterval} key={interval.key}>
                      <span>
                        {interval.start_time}–{interval.end_time}
                      </span>
                      <button
                        type="button"
                        className={classes.deleteButton}
                        disabled={deletingIds.includes(interval.id)}
                        onClick={() => removeInterval(interval)}
                        aria-label="Удалить интервал"
                      >
                        <DeleteOutlined />
                      </button>
                    </div>
                  ),
                )}
              </div>

              <button
                type="button"
                className={classes.addIntervalButton}
                onClick={() => addInterval(day.value)}
              >
                <PlusOutlined />
                Добавить
              </button>
            </div>
          )
        })}
      </div>

      <div className={classes.editorFooter}>
        <Button color="primary" variant="outline" onClick={onClose} disabled={loading}>
          Закрыть
        </Button>
        <Button
          color="primary"
          onClick={save}
          disabled={loading || hasErrors}
          isLoading={loading}
        >
          {newIntervals.length ? 'Сохранить интервалы' : 'Готово'}
        </Button>
      </div>
    </div>
  )
}

export default WorkHoursEditor
